/*
  Blog articles — original content for City Shopping Center & Dulha House.
  Each post: { slug, title, excerpt, date, readTime, sections: [{ heading, paragraphs: [] }] }
  To add a new article, copy the structure of any post below.
*/

const BLOG_POSTS = [
  {
    slug: "how-to-care-for-artificial-jewelry",
    title: "How to Care for Artificial Jewelry So It Shines for Years",
    excerpt:
      "Artificial jewelry can stay beautiful for a very long time — if you treat it right. Here is our complete care guide based on 20+ years of experience.",
    date: "2026-02-10",
    readTime: "6 min read",
    sections: [
      {
        heading: "",
        paragraphs: [
          "One of the most common questions our customers ask us is: \"How long will this jewelry last?\" The honest answer is that it depends far more on how you care for it than on the piece itself. We have customers who bought sets from us over ten years ago that still look brand new, and we have seen pieces ruined in a single month by perfume and sweat. This guide covers everything we tell our own family about keeping artificial jewelry beautiful.",
        ],
      },
      {
        heading: "Why Artificial Jewelry Tarnishes",
        paragraphs: [
          "Artificial jewelry — including American Diamond (AD), zircon, and fancy pieces — is usually made from a base metal such as brass or copper alloy, coated with a plated finish like gold, rhodium, or rose gold polish. The plating is what gives the piece its shine, and it is also the part that can wear away. Moisture, chemicals, and friction are the three enemies of plating. Once you understand that, all the care rules below make perfect sense.",
        ],
      },
      {
        heading: "Rule 1: Jewelry Goes On Last, Comes Off First",
        paragraphs: [
          "Perfume, hairspray, lotion, and makeup all contain chemicals and alcohols that eat into plating over time. The single most important habit you can build is this: finish getting ready completely — makeup, perfume, hairspray, everything — and only then put your jewelry on. When you come home, take your jewelry off before you wash your face or change clothes.",
          "This one habit alone will double or triple the life of your pieces.",
        ],
      },
      {
        heading: "Rule 2: Keep It Away from Water and Sweat",
        paragraphs: [
          "Never wear artificial jewelry while bathing, washing dishes, doing wuzu, or exercising. Water itself speeds up tarnishing, and sweat is even worse because it is slightly acidic and salty. If your jewelry does get wet, dry it immediately and thoroughly with a soft cotton cloth before storing it.",
          "In hot and humid weather, wipe your jewelry gently with a dry cloth as soon as you take it off. Those few seconds of care remove the invisible layer of sweat and oils that would otherwise sit on the plating all night.",
        ],
      },
      {
        heading: "Rule 3: Store Each Piece Separately",
        paragraphs: [
          "Throwing all your jewelry into one box is the fastest way to scratch it. Stones knock against each other, chains tangle, and plated surfaces rub together. Ideally, keep each set in its own small zip-lock pouch or the box it came in. Squeezing the air out of a zip-lock pouch before sealing it also protects the piece from humidity — a trick that works especially well during monsoon season.",
          "A few grains of uncooked rice or a small silica gel packet in your jewelry box absorbs moisture and keeps everything drier.",
        ],
      },
      {
        heading: "Rule 4: Clean Gently, Never with Chemicals",
        paragraphs: [
          "To clean artificial jewelry, use only a soft, dry, lint-free cloth — the kind used for spectacles works perfectly. Gently wipe the metal and stones. For dirt stuck in crevices, use a soft dry brush (a clean, dry toothbrush with soft bristles is fine).",
          "Never use silver polish, toothpaste, lemon, vinegar, or jewelry cleaning liquids made for real gold and silver. These are far too harsh for plated jewelry and can strip the finish in seconds.",
        ],
      },
      {
        heading: "What to Do If a Piece Has Already Tarnished",
        paragraphs: [
          "If a piece has darkened slightly, gentle buffing with a dry microfiber cloth can restore some shine. However, if the plating has genuinely worn through to the base metal, cleaning cannot bring it back — replating is the only real fix, and for most artificial pieces it is more economical to replace the item. This is exactly why prevention, using the rules above, matters so much.",
          "Cared for properly, a good quality artificial set can easily look beautiful for five to ten years of occasional wear — which makes it one of the smartest fashion investments you can make.",
        ],
      },
    ],
  },
  {
    slug: "american-diamond-vs-real-diamond",
    title: "American Diamond (AD) vs Real Diamond: What Is the Difference?",
    excerpt:
      "American Diamonds sparkle like the real thing at a fraction of the price. Here is what AD actually is, how it compares to real diamonds, and when it is the smarter choice.",
    date: "2026-03-05",
    readTime: "5 min read",
    sections: [
      {
        heading: "",
        paragraphs: [
          "Walk into any jewelry market in Pakistan and you will hear the term \"American Diamond\" or simply \"AD\" everywhere. Many first-time buyers assume it is a type of real diamond imported from America — it is not. Understanding what AD actually is will help you shop smarter and appreciate why it has become the most popular choice for fashion and bridal jewelry across South Asia.",
        ],
      },
      {
        heading: "What Is an American Diamond?",
        paragraphs: [
          "American Diamond is the popular market name for cubic zirconia (CZ) — a lab-created crystal made from zirconium dioxide. It was first produced commercially in the 1970s and quickly became the world's favorite diamond simulant because it is optically brilliant, hard enough for daily wear, and can be cut with the same facets as a real diamond.",
          "A well-cut CZ stone is genuinely difficult to tell apart from a diamond with the naked eye. It is colorless, full of fire, and catches light beautifully — which is exactly why AD jewelry photographs so well at weddings and events.",
        ],
      },
      {
        heading: "The Key Differences",
        paragraphs: [
          "Hardness: Real diamond is the hardest natural material (10 on the Mohs scale). CZ measures around 8 to 8.5 — still hard, but it can develop tiny scratches over many years of rough wear.",
          "Weight: CZ is noticeably heavier than diamond for the same size. Jewelers sometimes use this to tell them apart.",
          "Sparkle character: Diamonds give off more white brilliance, while CZ throws slightly more colorful, rainbow-like flashes. Most people actually find CZ's sparkle more eye-catching from a distance.",
          "Price: This is the big one. A real diamond ring can cost several lakh rupees; a stunning AD ring of the same visual size costs a tiny fraction of that. For occasion wear, the value difference is enormous.",
        ],
      },
      {
        heading: "When AD Is the Smarter Choice",
        paragraphs: [
          "For weddings, mehndi, parties, and gift-giving, AD jewelry is usually the more sensible option. You can own several complete matching sets — necklace, earrings, ring, bracelet — for less than the cost of a single small real-diamond item. There is also far less worry about loss or theft when traveling or attending large events.",
          "Fashion also changes. With AD, you can update your collection every season and match your jewelry to specific outfits without guilt.",
        ],
      },
      {
        heading: "How to Judge AD Quality",
        paragraphs: [
          "Not all AD jewelry is equal. Look for stones that are perfectly clear (not cloudy or milky), settings where every stone sits straight and secure, and plating that is even with no dull patches. Gently run a finger over the back of the piece — good finishing should feel smooth, not sharp or rough. A quality AD piece, cared for properly, will keep its sparkle for many years.",
        ],
      },
    ],
  },
  {
    slug: "how-to-find-your-ring-size-at-home",
    title: "How to Find Your Perfect Ring Size at Home (No Tools Needed)",
    excerpt:
      "Ordering a ring online? Here are three reliable ways to measure your ring size at home, plus the mistakes that lead to wrong sizes.",
    date: "2026-04-02",
    readTime: "5 min read",
    sections: [
      {
        heading: "",
        paragraphs: [
          "The most common problem with buying rings online is receiving one that does not fit. The good news is that you can measure your ring size at home quite accurately with things you already own. Here are the three methods we recommend to our customers, from most to least accurate.",
        ],
      },
      {
        heading: "Method 1: Use a Ring That Already Fits",
        paragraphs: [
          "If you own any ring that fits the intended finger well, this is the most reliable method. Place the ring on a ruler and measure the inside diameter in millimeters — from inner edge to inner edge, straight across the middle.",
          "As a quick reference: an inside diameter of about 16.5 mm is roughly size 6, 17.3 mm is size 7, 18.1 mm is size 8, and 19 mm is size 9. If your measurement falls between two sizes, choose the larger one.",
        ],
      },
      {
        heading: "Method 2: The String or Paper Strip Method",
        paragraphs: [
          "Cut a thin strip of paper or use a piece of thread. Wrap it snugly around the base of your finger — snug, but not tight. Mark the exact point where it overlaps, then measure that length in millimeters against a ruler. This is your finger circumference.",
          "Circumference guide: about 52 mm equals size 6, 54.5 mm equals size 7, 57 mm equals size 8, and 59.5 mm equals size 9. Repeat the measurement two or three times to make sure it is consistent.",
        ],
      },
      {
        heading: "Method 3: Compare with a Family Member",
        paragraphs: [
          "If the ring is a gift and you cannot measure the person's finger, borrow one of their rings (from the correct finger!) and use Method 1. If that is impossible, an average adjustable ring is often the safest gift choice — many of our fancy rings are adjustable for exactly this reason.",
        ],
      },
      {
        heading: "Mistakes That Cause Wrong Sizes",
        paragraphs: [
          "Fingers change size during the day. They are smallest in the morning and in cold weather, and largest in the evening, in heat, and after salty food. For the most accurate result, measure in the evening at normal room temperature.",
          "Also remember that your knuckle matters: the ring must slide over it. If your knuckle is noticeably wider than the base of your finger, measure both and pick a size in between.",
          "Finally, wider ring bands fit more snugly than thin ones. If you are choosing a broad ring design, consider going up half a size.",
        ],
      },
    ],
  },
  {
    slug: "necklace-lengths-guide",
    title: "Necklace Lengths Explained: From Choker to Rani Haar",
    excerpt:
      "Choker, princess, matinee, rani haar — necklace names can be confusing. This guide explains every length, what it suits, and how to pick the right one for your neckline.",
    date: "2026-05-14",
    readTime: "6 min read",
    sections: [
      {
        heading: "",
        paragraphs: [
          "The same necklace can look stunning on one outfit and completely wrong on another — and the reason is almost always length. Once you understand the standard necklace lengths and what each one pairs with, choosing jewelry for any outfit becomes easy. Here is the complete guide.",
        ],
      },
      {
        heading: "Choker (14–16 inches / 35–40 cm)",
        paragraphs: [
          "A choker sits high, wrapping closely around the middle of the neck. It is the star of modern bridal and party fashion, and it looks best with off-shoulder outfits, boat necks, deep necklines, and saree blouses. Chokers draw attention upward to the face and work beautifully on longer necks. If you have a shorter neck, choose a slim, delicate choker rather than a very broad one.",
        ],
      },
      {
        heading: "Princess Length (17–19 inches / 43–48 cm)",
        paragraphs: [
          "This is the most versatile and most popular length — the necklace sits just on or slightly below the collarbone. Almost every classic pendant set falls in this range. Princess length suits practically every neckline and every face shape, which makes it the safest choice when buying a gift.",
        ],
      },
      {
        heading: "Matinee Length (20–24 inches / 50–60 cm)",
        paragraphs: [
          "Matinee necklaces fall between the collarbone and the bust. They pair wonderfully with high necklines, kurtis, and formal western wear where a shorter necklace would get lost against the fabric. A matinee chain with a statement pendant creates a graceful, elongating effect.",
        ],
      },
      {
        heading: "Rani Haar / Opera Length (28 inches and longer)",
        paragraphs: [
          "The rani haar — literally \"queen's necklace\" — is the long, regal layered necklace of traditional South Asian bridal jewelry, usually reaching to the bust or below. It is designed to be worn over heavy bridal outfits, often layered with a choker or princess-length set for the classic maharani look. If you are a bride, the choker-plus-rani-haar combination is the timeless formula.",
        ],
      },
      {
        heading: "Matching Length to Neckline: Quick Rules",
        paragraphs: [
          "Deep or sweetheart neckline: choker or princess length so the necklace sits on skin, not fabric. High neck or band collar: skip short necklaces entirely and wear a matinee or long chain over the fabric. Boat neck: choker. V-neck: a princess-length pendant that echoes the V shape looks perfect.",
          "One final tip: when layering multiple necklaces, keep at least two inches of difference between each piece so every layer stays visible.",
        ],
      },
    ],
  },
  {
    slug: "bridal-jewelry-guide",
    title: "The Complete Bridal Jewelry Guide: What Every Bride Needs",
    excerpt:
      "From baraat to walima, every wedding event calls for a different jewelry approach. Here is our practical checklist for building a complete bridal jewelry collection.",
    date: "2026-06-20",
    readTime: "7 min read",
    sections: [
      {
        heading: "",
        paragraphs: [
          "Bridal jewelry shopping can feel overwhelming — there are so many pieces, so many events, and so much advice. After more than two decades of helping brides and their families, we have refined it down to a simple, practical system. This guide walks you through what you actually need for each event and how to make smart choices that photograph beautifully.",
        ],
      },
      {
        heading: "Start with the Outfits, Not the Jewelry",
        paragraphs: [
          "The number one rule: finalize your outfits first. Jewelry should be chosen to match the outfit's color, embroidery tone (gold vs silver work), and neckline. Bring photos of your outfits — or ideally the dupatta itself — when you shop. Gold-tone jewelry pairs with gold and copper embroidery; silver, rhodium, and white AD jewelry pairs with silver work and pastel outfits.",
        ],
      },
      {
        heading: "The Baraat / Nikkah Set: Go Traditional",
        paragraphs: [
          "The main wedding day calls for the fullest set: a choker plus rani haar combination, a matching jhoomar and teeka, chandelier or jhumka earrings, and a nath if it suits your look. Deep traditional tones — gold with red, maroon, or emerald stones — photograph richly against classic bridal reds and maroons.",
          "Weight matters more than brides expect. You will wear this jewelry for six to eight hours. Always try the full set together and move your head; if the earrings pull uncomfortably in the shop, they will be painful by dinner. Ear chains (sahara) that hook into the hair take most of the weight off the earlobe and are worth adding to heavy earrings.",
        ],
      },
      {
        heading: "Mehndi: Light, Colorful, and Fun",
        paragraphs: [
          "Mehndi jewelry should let you dance. Choose lightweight floral-style pieces, colorful stones (pinks, greens, multi-color), and comfortable earrings. Many brides now pair fresh or artificial flowers with light AD or fancy jewelry — a beautiful combination that costs very little. Bangles in mixed colors complete the look.",
        ],
      },
      {
        heading: "Walima: Elegant and Modern",
        paragraphs: [
          "Walima outfits are usually pastel — ice blue, mint, silver, blush pink — and the jewelry should follow. This is where American Diamond truly shines: a full AD set with white or champagne stones looks incredibly elegant against pastel fabrics and modern silhouettes. A princess-length AD necklace with matching drops is the most flattering, camera-friendly formula.",
        ],
      },
      {
        heading: "Do Not Forget These Small Pieces",
        paragraphs: [
          "Brides remember the necklace and forget the details. Your checklist should also include: a ring for each event (or one statement adjustable ring), bangles or kangans sized to slide over your hand comfortably, a bracelet for the walima look, and backup earring backs — they are tiny, they get lost, and having spares saves the day.",
        ],
      },
      {
        heading: "A Note on Budget",
        paragraphs: [
          "High-quality artificial bridal jewelry lets you have a complete, distinct look for every single event — often for less than the cost of one real gold bangle. Spend the most on your baraat set since it appears in the most photographs, keep mehndi jewelry light and inexpensive, and choose a walima set you will genuinely re-wear at future weddings and formal events. Beautiful bridal jewelry is not about spending the most; it is about choosing pieces that match your outfits, fit comfortably, and shine in photographs.",
        ],
      },
    ],
  },
];

export default BLOG_POSTS;
